// Minimal HTML5 audio player wrapping a take's playback URL.
//
// Used inside the take-review modal. Adds keyboard shortcuts for play/pause
// (Space) and rewind 1s (Left). Does NOT manage URL freshness — if the
// pre-signed URL expires, the parent re-fetches via `useReviewTake`.

import { Button } from '@ohear/ui'
import { useEffect, useRef, useState } from 'react'

export interface TakeAudioPlayerProps {
  audioUrl: string | null
}

export function TakeAudioPlayer({ audioUrl }: TakeAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    const onTime = () => setCurrentTime(el.currentTime)
    const onMeta = () => setDuration(el.duration || 0)
    const onEnded = () => setPlaying(false)
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('ended', onEnded)
    return () => {
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('ended', onEnded)
    }
  }, [audioUrl])

  const togglePlay = () => {
    const el = audioRef.current
    if (!el) return
    if (playing) {
      el.pause()
      setPlaying(false)
    } else {
      void el.play().then(() => setPlaying(true))
    }
  }

  if (!audioUrl) {
    return (
      <p className="text-[13px] text-[var(--text-2)]">
        Audio is still loading. The presigned URL will appear shortly.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      <div className="flex items-center gap-3">
        <Button size="sm" variant="ghost" onClick={togglePlay} aria-pressed={playing}>
          {playing ? 'Pause' : 'Play'}
        </Button>
        <span className="font-mono text-[12px] tabular-nums text-[var(--text-2)]">
          {formatSec(currentTime)} / {formatSec(duration)}
        </span>
      </div>
    </div>
  )
}

function formatSec(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00'
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
